import React, { useState } from "react";
import styles from "./WalletBalanceCard.module.scss";
import { CustomerWalletDetails } from "../models/CustomerWalletDetails";
import { handleCopy } from "../../../../utils";
import { useSelector } from "react-redux";
import { RootState } from "../../../../stores/store";
import { CountryCode, countryCurrencies } from "../../../../model/common/country";
import { useTranslation } from "react-i18next";
import { CopyIcon, EyeClosedIcon, CaretDownIcon, EyeOpenedIcon, LightsBacgroundIcon } from "../../../../assets/icons";

interface Props {
	data: CustomerWalletDetails;
	showCreatedBanner: boolean;
}

export const WalletBalanceCard: React.FC<Props> = ({ data, showCreatedBanner }) => {
	const [isBalanceVisible, setIsBalanceVisible] = useState(true);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [showMoreOptions, setShowMoreOptions] = useState(false);
	const countryCode = useSelector((state: RootState) => state.user.countryCode);
	const { t } = useTranslation();

	const selectedWallet = data.walletDetails[selectedIndex] ?? data.walletDetails[0];
	if (!selectedWallet) return null;

	const currency = countryCurrencies[countryCode as CountryCode] ?? "NGN";

	const formatBalance = (amount: number) => new Intl.NumberFormat("en-NG", { style: "currency", currency, minimumFractionDigits: 2 }).format(amount);

	const currencySymbol = (() => {
		try {
			return new Intl.NumberFormat("en-NG", { style: "currency", currency }).formatToParts(0).find(p => p.type === "currency")?.value ?? "₦";
		} catch {
			return "₦";
		}
	})();

	return (
		<div className={styles.card}>
			<LightsBacgroundIcon className={styles.bgDecoration} />

			<div className={styles.topSection}>
				<div className={styles.topRow}>
					<span className={styles.balanceLabel}>{t("wallet.walletActiveAvailableBalance")}</span>
				</div>

				<div className={styles.balanceRow}>
					<span className={styles.balanceAmount}>{isBalanceVisible ? formatBalance(selectedWallet.availableBalance) : `${currencySymbol} *****`}</span>
					<button className={styles.eyeBtn} onClick={() => setIsBalanceVisible(v => !v)} aria-label={t("wallet.web.toggleBalanceVisibility")}>
						{isBalanceVisible ? <EyeOpenedIcon /> : <EyeClosedIcon />}
					</button>
				</div>

				{showCreatedBanner && (
					<div className={styles.createdBanner}>
						<div className={styles.checkCircle}>✓</div>
						<span>{t("wallet.web.walletCreatedSuccessfully")}</span>
					</div>
				)}
			</div>

			<div className={styles.accountSectionHelper}>
				<div className={styles.fundLabel}>{t("wallet.walletActiveFundWalletWithVirtualAccountNumber")}</div>
			</div>
			<div className={styles.accountSection}>
				<div className={styles.accountRow}>
					<div className={styles.accountInfo}>
						<span className={styles.bankAndName}>
							{selectedWallet.bankName} — {data.firstName} {data.lastName}
						</span>
						<span className={styles.accountNumber}>{selectedWallet.accountNumber}</span>
					</div>
					<button
						className={styles.copyBtn}
						onClick={() => {
							handleCopy(selectedWallet.accountNumber);
						}}
					>
						<CopyIcon />
						{t("common.actions.copy")}
					</button>
				</div>

				{data.walletDetails.length > 1 && (
					<div className={styles.moreOptionsContainer}>
						<button className={styles.moreOptionsBtn} onClick={() => setShowMoreOptions(v => !v)}>
							{t("wallet.web.seeMoreOptions")}
							<CaretDownIcon style={{ transform: showMoreOptions ? "rotate(180deg)" : undefined, transition: "transform 0.2s" }} />
						</button>
						{showMoreOptions && (
							<div className={styles.optionsDropdown}>
								{data.walletDetails.map((wallet, idx) => (
									<button
										key={wallet.id}
										className={`${styles.optionItem} ${idx === selectedIndex ? styles.selected : ""}`}
										onClick={() => {
											setSelectedIndex(idx);
											setShowMoreOptions(false);
										}}
									>
										{wallet.bankName} — {wallet.accountNumber}
									</button>
								))}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
